<template>
    <div>
        <progress-bar
            v-if="currentStep !== 4"
            :key="currentStep + ' ' + currentSubStep"
            class="mt-1 p-1"
            :steps="steps"
            :current-step="currentStep"
            :current-sub-step="currentSubStep"
            @click="progressBarClick"
        />
        <AlertMessage
            v-if="hasError"
            :title="messages.error"
            type="danger"
        />

        <div class="mb-2">
            <template v-if="currentStep === 1">
                <template v-if="!selectedCategory">
                    <p v-html="messages.step1Title" />
                    <div class="categories-wrapper">
                        <button class="ob1-card category" @click="selectCategory('mobile')">
                            <span class="lead d-block" v-html="messages.categories.mobile.label" />
                            <img class="pb-1" :src="messages.categories.mobile.imgPath" alt="">
                        </button>
                        <button class="ob1-card category" @click="selectCategory('tablette')">
                            <span class="lead d-block" v-html="messages.categories.tablet.label" />
                            <img class="pb-1" :src="messages.categories.tablet.imgPath" alt="">
                        </button>
                        <button class="ob1-card category" @click="selectCategory('objets')">
                            <span class="lead d-block" v-html="messages.categories.connectedObject.label" />
                            <img class="pb-1" :src="messages.categories.connectedObject.imgPath" alt="">
                        </button>
                        <button class="ob1-card category" @click="selectCategory('imei')">
                            <span class="lead d-block" v-html="messages.categories.imei.label" />
                            <img class="pb-1" :src="messages.categories.imei.imgPath" alt="">
                        </button>
                    </div>
                </template>

                <template v-if="selectedCategory">
                    <recovery-simulator-selected-category
                        v-if="selectedCategory ==='mobile'"
                        :title="messages.categories.mobile.label"
                        :image="messages.categories.mobile.imgPath"
                        :back-link-label="messages.categories.backLabel"
                        @back="resetToFirstStep"
                    >
                        <div v-html="messages.categories.mobile.description" />
                    </recovery-simulator-selected-category>
                    <recovery-simulator-selected-category
                        v-if="selectedCategory ==='tablette'"
                        :title="messages.categories.tablet.label"
                        :image="messages.categories.tablet.imgPath"
                        :back-link-label="messages.categories.backLabel"
                        @back="resetToFirstStep"
                    >
                        <div v-html="messages.categories.tablet.description" />
                    </recovery-simulator-selected-category>
                    <recovery-simulator-selected-category
                        v-if="selectedCategory ==='objets'"
                        :title="messages.categories.connectedObject.label"
                        :image="messages.categories.connectedObject.imgPath"
                        :back-link-label="messages.categories.backLabel"
                        @back="resetToFirstStep"
                    >
                        <div v-html="messages.categories.connectedObject.description" />
                    </recovery-simulator-selected-category>
                    <recovery-simulator-selected-category
                        v-if="selectedCategory ==='imei'"
                        :title="messages.categories.imei.label"
                        :image="messages.categories.imei.imgPath"
                        :back-link-label="messages.categories.backLabel"
                        @back="resetToFirstStep"
                    >
                        <div v-html="messages.categories.imei.description" />
                    </recovery-simulator-selected-category>

                    <template v-if="selectedCategory === 'imei'">
                        <p class="lead mt-2 mb-0">
                            <strong v-html="messages.searchImei" />
                        </p>
                        <recovery-simulator-by-imei
                            :shop="shop"
                            :messages="messages"
                            @selectModel="onSelectModel"
                            @error="onError"
                        />
                    </template>
                    <template v-else>
                        <p class="lead mt-2 mb-0">
                            <strong v-html="messages.searchBrand" />
                        </p>
                        <recovery-simulator-by-brand
                            :shop="shop"
                            :category="selectedCategory"
                            :messages="messages"
                            @selectModel="onSelectModel"
                            @error="onError"
                        />
                    </template>
                </template>
            </template>
            <template v-if="currentStep === 2">
                <p v-html="messages.step2Title" />
                <fieldset>
                    <legend class="h4" v-html="messages.questions.question1Label" />
                    <div v-html="messages.questions.question1Description" />

                    <recovery-simulator-question-radio
                        id="question1"
                        key="question1"
                        v-model="answer1"
                        name="question1"
                    />
                </fieldset>
                <fieldset>
                    <legend class="h4" v-html="messages.questions.question2Label" />
                    <div v-html="messages.questions.question2Description" />

                    <recovery-simulator-question-radio
                        id="question2"
                        key="question2"
                        v-model="answer2"
                        name="question2"
                    />
                </fieldset>
            </template>
            <template v-if="currentStep === 3">
                <p v-html="messages.step3Title" />
                <fieldset>
                    <legend class="h4" v-html="messages.questions.question3Label" />
                    <div v-html="messages.questions.question3Description" />
                    <recovery-simulator-question-radio
                        id="question3"
                        key="question3"
                        v-model="answer3"
                        name="question3"
                    />
                </fieldset>
                <p class="h4" v-html="messages.questions.question4Label" />
                <div v-html="messages.questions.question4Description" />
            </template>
        </div>
        <div id="continue-button" class="bg-white pb-1">
            <hr class="separator separator--gray4 mb-1 d-md-none">
            <div id="buttons-container" :class="flexJustify">
                <Lien
                    v-if="currentStep > 1"
                    :label="messages.backButtonLabel"
                    is-link-arrow-back
                    is-mini
                    is-button
                    @click="goToPreviousStep()"
                />
                <button
                    type="button"
                    href="#"
                    :disabled="!buttonEnabled"
                    class="d-block btn btn-primary"
                    @click="goToNextStep()"
                    v-html="buttonLabel"
                />
            </div>
        </div>
    </div>
</template>

<script>
import RecoverySimulatorByBrand from "./RecoverySimulatorByBrand.vue";
import RecoverySimulatorByImei from "./RecoverySimulatorByImei.vue";
import RecoverySimulatorSelectedCategory from "./RecoverySimulatorSelectedCategory.vue";
import RecoverySimulatorQuestionRadio from "./RecoverySimulatorQuestionRadio.vue";
import {toTmsFormat} from "../../../common/utils/TmsUtils";
import AlertMessage from "../../../common/components/ob1/AlertMessage.vue";
import Lien from "../../../common/components/ob1/Lien.vue";
import ProgressBar from "../../../common/ob1/component/ProgressBar.vue";

export default {
    name: "RecoverySimulator",
    components: {
        Lien,
        AlertMessage,
        ProgressBar,
        RecoverySimulatorQuestionRadio,
        RecoverySimulatorSelectedCategory,
        RecoverySimulatorByImei,
        RecoverySimulatorByBrand
    },
    props: {
        modalId: {
            type: String,
            required: true
        },
        shop: {
            type: String,
            required: true
        },
        messages: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            steps: [
                {
                    label: this.messages.step1Label,
                    subStepsCount: 2
                },
                {
                    label: this.messages.step2Label
                },
                {
                    label: this.messages.step3Label
                }
            ],
            currentStep: 1,
            currentSubStep: 1,
            currentStepLabel: this.messages.step1Label,
            buttonLabel: this.messages.step1Button,
            selectedCategory: undefined,
            selectedBrand: undefined,
            selectedModel: undefined,
            answer1: undefined,
            answer2: undefined,
            answer3: undefined,
            recoveryPriceInCents: undefined,
            hasError: false
        };
    },
    computed: {
        step1Completed: function() {
            return this.selectedBrand !== undefined && this.selectedModel !== undefined;
        },
        step2Completed: function() {
            return this.step1Completed && this.answer1 !== undefined && this.answer2 !== undefined;
        },
        step3Completed: function() {
            return this.step2Completed && this.answer3 !== undefined;
        },
        buttonEnabled: function() {
            return (this.currentStep === 1 && this.step1Completed)
                || (this.currentStep === 2 && this.step2Completed)
                || (this.currentStep === 3 && this.step3Completed)
                || this.currentStep === 4;
        },
        tmsLabelStep1: function() {
            let label = "";
            if (this.step1Completed) {
                label += "_" + toTmsFormat(this.selectedCategory);
                label += ";" + toTmsFormat(this.selectedBrand);
                label += ";" + toTmsFormat(this.selectedModel);
            }
            return label;
        },
        tmsLabelStep2: function() {
            let label = "";
            if (this.step2Completed) {
                label += "_" + toTmsFormat(this.messages.questions.question1Label);
                label += "_" + this.booleanForTms(this.answer1);
                label += ";" + toTmsFormat(this.messages.questions.question2Label);
                label += "_" + this.booleanForTms(this.answer2);
            }
            return label;
        },
        tmsLabelStep3: function() {
            let label = "";
            if (this.step3Completed && this.recoveryPriceInCents !== undefined) {
                label += "_" + toTmsFormat(this.messages.questions.question3Label);
                label += "_" + this.booleanForTms(this.answer3);
                label += "_" + this.recoveryPriceInCents / 100 + "_euros";
            }
            return label;
        },
        flexJustify: function() {
            return this.currentStep > 1 ? 'flex-justify-space-between' : 'flex-justify-end'
        }
    },
    methods: {
        toTmsFormat,
        reset() {
            this.currentStep = 1;
            this.currentSubStep = 1;
            this.currentStepLabel = this.messages.step1Label;
            this.buttonLabel = this.messages.step1Button;
            this.selectedCategory = undefined;
            this.selectedBrand = undefined;
            this.selectedModel = undefined;
            this.answer1 = undefined;
            this.answer2 = undefined;
            this.answer3 = undefined;
            this.hasError = false;
        },
        selectCategory(category) {
            this.currentSubStep = 2;
            this.selectedCategory = category;
        },
        onSelectModel(brand, model) {
            this.hasError = false;
            this.selectedBrand = brand;
            this.selectedModel = model;
        },
        onError() {
            this.hasError = true;
        },
        goToStep2(subStepNumber) {
            this.currentStep = 2;
            if (subStepNumber) {
                this.currentSubStep = subStepNumber;
            } else {
                this.currentSubStep = 1;
            }
            this.currentStepLabel = this.messages.step2Label;
            this.buttonLabel = this.messages.step2Button;
        },
        goToStep3() {
            this.currentStep = 3;
            this.currentSubStep = 1;
            this.currentStepLabel = this.messages.step3Label;
            this.buttonLabel = this.messages.step3Button;
        },
        goToNextStep() {
            let category = toTmsFormat(this.messages.title);
            let action = toTmsFormat(this.currentStepLabel);
            let buttonLabel = toTmsFormat(this.buttonLabel);

            if (this.currentStep === 1 && this.step1Completed) {
                Tms.link({ category: category, action: action, label: buttonLabel + "_" + this.tmsLabelStep1 });
                this.goToStep2();
            } else if (this.currentStep === 2 && this.step2Completed) {
                Tms.link({ category: category, action: action, label: buttonLabel + "_" + this.tmsLabelStep2 });
                this.goToStep3();
            } else if (this.currentStep === 3 && this.step3Completed) {
                this.fetchRecoveryPrice(this.shop, this.selectedBrand, this.selectedModel, this.answer1, this.answer2, this.answer3)
                    .then(result => {
                        this.recoveryPriceInCents = result;
                        this.currentStep = 4;
                        this.buttonLabel = this.messages.step4Button;

                        Tms.link({ category: category, action: action, label: buttonLabel + "_" + this.tmsLabelStep3 });

                        this.$emit('update-recovery-simulation', this.recoveryPriceInCents, this.selectedModel);
                    }).catch(() => {
                        this.hasError = true;
                })
            }
        },
        goToPreviousStep() {
            this.goToStep(this.currentStep - 1);
        },
        progressBarClick(stepNumber, subStepNumber) {
            const category = toTmsFormat(this.messages.title);

            const currentStepName = toTmsFormat(this.steps[this.currentStep - 1].label);
            const action = "etapier_je_suis_a_l_etape_" + currentStepName;

            let label;
            if (stepNumber === this.currentStep) {
                label = "retour_a_la_sous_etape_precedente";
            } else {
                const clickedStepName = toTmsFormat(this.steps[stepNumber - 1].label);
                label = "etapier_vers_" + clickedStepName;
            }

            Tms.link({category: category, action: action, label: label});

            this.goToStep(stepNumber, subStepNumber);
        },
        goToStep(stepNumber, subStepNumber) {
            if (stepNumber === 1) {
                this.goToFirstStep(subStepNumber);
            } else if (stepNumber === 2) {
                this.goToStep2(subStepNumber);
            }
        },
        resetToFirstStep() {
            this.reset();
        },
        goToFirstStep(subStepNumber) {
            if (subStepNumber > 1) {
                this.currentStep = 1;
                this.currentSubStep = subStepNumber;
            } else {
                this.resetToFirstStep();
            }
        },
        booleanForTms(value) {
            return value ? "oui" : "non";
        },
        fetchRecoveryPrice: function(shop, brand, model, answer1, answer2, answer3) {
            return $.ajax({
                method: "GET",
                data: {
                    "shop": shop,
                    "brand": brand,
                    "model": model,
                    "answer1": answer1,
                    "answer2": answer2,
                    "answer3": answer3
                },
                url: "/api/recovery/price",
                contentType: "application/json",
                dataType: "json"
            });
        }
    }
};
</script>

<style lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

.categories-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: px-to-rem(30);
}

@media (min-width: #{map-get($grid-breakpoints, lg)}) {
    .categories-wrapper {
        grid-template-columns: repeat(4, 1fr);
    }
}

.category {
    background-color: white;
}

.category img {
    height: px-to-rem(110);
}

#continue-button {
    @media (max-width: #{map-get($grid-breakpoints, md)}) {
        position: sticky;
        bottom: 0px;
        z-index: 3;
    }
}

#continue-button button.d-block {
    @media (max-width: #{map-get($grid-breakpoints, md)}) {
        width: 100%;
    }
}

#buttons-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: px-to-rem(10);
    @media (max-width: #{map-get($grid-breakpoints, md)}) {
        width: 100%;
        flex-direction: column-reverse;
    }
    &.flex-justify-end {
        justify-content: end;
    }

    &.flex-justify-space-between {
        justify-content: space-between;
    }

}


</style>
